import React from 'react';
import { ListSubheader, MenuItem, Popover, TextField, Typography } from '@mui/material';
import { useAttributeOptions } from './useAttributeOptions';
import { useTokenFieldContext } from './TokenFieldContext';
import type { AttributeScope } from '../../../Campaigns/guided/config/attributes';

/**
 * The searchable, grouped variable picker shown when an author opens the `{{` menu — the shared UI behind
 * both insert paths (the type-`{{` trigger and the "Insert reference" button). Anchored to the field, it
 * lists every `{{group.key}}` variable available at this authoring point (from {@link useAttributeOptions},
 * DB-driven), filterable by a search box, and calls `onPick` with the chosen canonical token. Kept as its
 * own component so the option list + search state live in one place rather than duplicated across the
 * controlled ({@link useTokenAutocomplete}) and drop-in ({@link TokenAwareTextField}) hosts.
 */
export function TokenPickerPopover(props: {
  open: boolean;
  anchorEl: HTMLElement | null;
  campaignId: number | undefined;
  uptoStepOrder?: number;
  /** Authoring scope for the group filter; defaults to the ambient {@link TokenFieldContext} scope. */
  scope?: AttributeScope;
  onPick: (token: string) => void;
  onClose: () => void;
}): JSX.Element {
  const { open, anchorEl, campaignId, uptoStepOrder, scope, onPick, onClose } = props;
  const ctx = useTokenFieldContext();
  const groups = useAttributeOptions(campaignId, { uptoStepOrder, scope: scope ?? ctx.scope });
  const [query, setQuery] = React.useState('');

  // Reset the filter each time the picker opens so a stale search never hides the options.
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const q = query.trim().toLowerCase();
  const filteredGroups = React.useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.token.toLowerCase().includes(q) ||
            (o.legacyAlias ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, q]);

  // MUI's Popover/Menu list takes a flat child list; interleave a subheader per group with its items.
  const items: React.ReactNode[] = [];
  filteredGroups.forEach((g) => {
    items.push(
      <ListSubheader key={`h-${g.group}`} disableSticky>
        {g.groupLabel}
      </ListSubheader>,
    );
    g.options.forEach((opt, i) => {
      items.push(
        <MenuItem
          key={`${g.group}-${i}-${opt.token}`}
          onClick={() => onPick(opt.token)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.25,
            py: 0.75,
          }}
        >
          <Typography variant="body2">{opt.label}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, fontFamily: 'monospace' }}>
            {opt.token}
            {opt.legacyAlias ? ` · ${opt.legacyAlias}` : ''}
          </Typography>
        </MenuItem>,
      );
    });
  });

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ style: { maxHeight: 420, width: 360 } }}
    >
      <div style={{ padding: 8, position: 'sticky', top: 0, background: 'inherit', zIndex: 1 }}>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="Search variables…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        />
      </div>
      {items.length > 0 ? items : <MenuItem disabled>No variables available</MenuItem>}
    </Popover>
  );
}

export default TokenPickerPopover;
